import React, { useState, useEffect, useCallback } from "react";
import { useParams, useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import ArrowBackIcon from "@material-ui/icons/ArrowBackIosOutlined";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/DeleteOutline";
import ReportIcon from "@material-ui/icons/AssessmentOutlined";
import { format } from "date-fns";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { useConfirm } from "../../components/ConfirmDialog";

const T = { bg: "#F6F7F9", panel: "#FFFFFF", line: "#E6E9ED", hover: "#FAFBFC", ink: "#1B222A", ink2: "#5B6470", ink3: "#929AA4" };

const useStyles = makeStyles(theme => ({
  page: { background: T.bg, minHeight: "100%" },
  wrap: { maxWidth: 1080, margin: "0 auto", padding: theme.spacing(3) },
  top: { display: "flex", alignItems: "center", gap: theme.spacing(1), marginBottom: theme.spacing(2) },
  title: { fontFamily: theme.app.heading, fontSize: 22, fontWeight: 700, color: theme.app.ink },
  grow: { flex: 1 },
  list: { display: "flex", flexDirection: "column", gap: 10 },
  card: { display: "flex", alignItems: "center", gap: theme.spacing(2), background: T.panel, border: `1px solid ${T.line}`, borderRadius: 12, padding: theme.spacing(1.5, 2), cursor: "pointer", "&:hover": { background: T.hover } },
  avatar: { width: 40, height: 40, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", background: "#EEF2FB", color: "#3B82C4", flexShrink: 0 },
  main: { flex: 1, minWidth: 0 },
  name: { fontSize: 15, fontWeight: 600, color: T.ink },
  sub: { fontSize: 12.5, color: T.ink3 },
  empty: { textAlign: "center", color: T.ink3, padding: theme.spacing(8, 2) },
}));

const DatasetReports = () => {
  const classes = useStyles();
  const history = useHistory();
  const { id } = useParams();
  const { confirm } = useConfirm();

  const [ds, setDs] = useState(null);
  const [reports, setReports] = useState([]);

  const load = useCallback(async () => {
    try {
      const [{ data: d }, { data: r }] = await Promise.all([
        api.get(`/datasets/${id}`),
        api.get("/reports", { params: { datasetId: id } }),
      ]);
      setDs(d.dataset);
      setReports(r.reports || []);
    } catch (err) {
      toastError(err);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const remove = async (e, r) => {
    e.stopPropagation();
    if (!(await confirm({ title: "Excluir relatório", message: `Excluir "${r.name}"?`, confirmText: "Excluir", danger: true }))) return;
    try {
      await api.delete(`/reports/${r.id}`);
      load();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <div className={classes.page}>
      <div className={classes.wrap}>
        <div className={classes.top}>
          <IconButton size="small" onClick={() => history.push(`/datasets/${id}`)}><ArrowBackIcon fontSize="small" /></IconButton>
          <Typography className={classes.title}>Relatórios · {ds ? ds.name : "…"}</Typography>
          <div className={classes.grow} />
          <Button variant="contained" color="primary" disableElevation startIcon={<AddIcon />} onClick={() => history.push(`/datasets/${id}/report`)}>
            Novo relatório
          </Button>
        </div>

        {reports.length === 0 ? (
          <div className={classes.empty}>Nenhum relatório ainda. Crie um recorte sobre esta tabela.</div>
        ) : (
          <div className={classes.list}>
            {reports.map(r => (
              <div key={r.id} className={classes.card} onClick={() => history.push(`/datasets/${id}/report/${r.id}`)}>
                <div className={classes.avatar}><ReportIcon /></div>
                <div className={classes.main}>
                  <div className={classes.name}>{r.name}</div>
                  <div className={classes.sub}>
                    Atualizado {r.updatedAt ? format(new Date(r.updatedAt), "dd/MM/yyyy HH:mm") : "—"}
                    {r.createdBy === "copiloto" ? " · pelo copiloto" : ""}
                  </div>
                </div>
                <IconButton size="small" onClick={e => remove(e, r)}><DeleteIcon fontSize="small" /></IconButton>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DatasetReports;
